import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress, Alert, Snackbar,
  Stack, useMediaQuery, Button, Chip, Paper
} from '@mui/material';
import {
  Person, LocationOn, Assessment, CheckCircle, PendingActions, SupervisorAccount,
  Refresh, ArrowForward
} from '@mui/icons-material';
import { getTflMonitoring } from '../API/tflMonitoring';

const KoordTflDashboard = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.authAdmin?.user);
  const userKecamatan = user?.kecamatan || '';
  const isMobile = useMediaQuery('(max-width:800px)');

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'success' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTflMonitoring();
      setRows(res.data.data || []);
      setSummary(res.data.summary || null);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, msg: 'Gagal memuat data', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  const totalBantuanSelesai = rows.reduce((s, t) => s + (t.statusCount['Selesai'] || 0), 0);
  const totalBantuanProses = rows.reduce((s, t) => s + (t.statusCount['Dalam Proses'] || 0), 0);
  const totalBantuanUsulan = rows.reduce((s, t) => s + (t.statusCount['Usulan Diterima'] || 0), 0);

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              <SupervisorAccount sx={{ mr: 1, verticalAlign: 'middle' }} />
              Dashboard Koordinator TFL
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Selamat datang, {user?.username}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip icon={<LocationOn />} label={`Kecamatan: ${userKecamatan || '-'}`} color="primary" variant="outlined" />
            <Button variant="outlined" startIcon={<Refresh />} size="small" onClick={fetchData}>Muat Ulang</Button>
          </Stack>
        </Box>

        {!userKecamatan && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Kecamatan pantau belum diatur untuk akun Anda. Hubungi admin untuk mengatur kecamatan.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Person color="primary" />
                  <Typography variant="h5" fontWeight={700}>{summary?.totalTFL || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Total TFL</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <Assessment color="info" />
                  <Typography variant="h5" fontWeight={700}>{summary?.totalBantuan || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">Total Bantuan</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <CheckCircle color="success" />
                  <Typography variant="h5" fontWeight={700} color="success.main">{totalBantuanSelesai}</Typography>
                  <Typography variant="caption" color="text.secondary">Bantuan Selesai</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card><CardContent sx={{ textAlign: 'center' }}>
                  <PendingActions color="warning" />
                  <Typography variant="h5" fontWeight={700} color="warning.main">{totalBantuanProses + totalBantuanUsulan}</Typography>
                  <Typography variant="caption" color="text.secondary">Dalam Proses</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>

            {/* Quick Actions */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                  onClick={() => navigate('/koordinator-tfl/monitoring')}>
                  <Box>
                    <SupervisorAccount color="primary" />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5 }}>Monitoring TFL</Typography>
                    <Typography variant="caption" color="text.secondary">Pantau aktivitas TFL di kecamatan {userKecamatan}</Typography>
                  </Box>
                  <ArrowForward color="action" />
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' } }}
                  onClick={() => navigate('/proses-bantuan')}>
                  <Box>
                    <Assessment color="success" />
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5 }}>Proses Bantuan</Typography>
                    <Typography variant="caption" color="text.secondary">Validasi langkah administrasi bantuan</Typography>
                  </Box>
                  <ArrowForward color="action" />
                </Paper>
              </Grid>
            </Grid>

            {/* TFL List Preview */}
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>TFL di Kecamatan {userKecamatan}</Typography>
                  <Button size="small" endIcon={<ArrowForward />} onClick={() => navigate('/koordinator-tfl/monitoring')}>Lihat Semua</Button>
                </Box>
                {rows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    Belum ada TFL dengan bantuan di kecamatan {userKecamatan}.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {rows.slice(0, 5).map((tfl) => (
                      <Paper key={tfl.id} variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{tfl.username}</Typography>
                          <Typography variant="caption" color="text.secondary">WA: {tfl.NoWa || '-'}</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip size="small" label={`${tfl.totalBantuan} bantuan`} color="primary" variant="outlined" />
                          <Chip size="small" label={`${tfl.avgProgress}%`} color={tfl.avgProgress >= 100 ? 'success' : 'warning'} />
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </>
        )}

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.sev} onClose={() => setSnackbar(p => ({ ...p, open: false }))} variant="filled">
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default KoordTflDashboard;
